import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery, useQueryClient } from 'react-query'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAppNavigate } from '../../../utils/url'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'
import { formatDate } from '../../../utils/formatting'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { Sponsor } from '../../../models/Sponsor.model'
import { SponsorRelations } from '../../../models/SponsorRelations.model'

import UserService from '../../../services/UserService/UserService'
import { SponsorService } from '../../../services/SponsorService/SponsorService'

import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import SponsorForm from '../shared/SponsorForm.component'
import { AppStateContext } from '../../../providers/AppStateContext'
import LightboxImage from '../../../components/LightboxImage/LightboxImage.component'
import DetailSection from '../../../components/Sections/DetailSection.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { useEditSectionParam } from '../../../components/Sections/useEditSectionParam'
import { DetailField, DetailFieldGrid } from '../../../components/Sections/DetailField.component'
import { SectionDefinition } from '../../../components/Sections/Sections.types'
import { SponsorSectionIds, sponsorSections } from '../shared/SponsorSections'

const SponsorDetail = (): JSX.Element => {
  const navigate = useAppNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingError, setLoadingError] = useState('')

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editSponsor])
  const hasTrailPermissions = UserService.hasPermissions(user, [AppPermissions.viewTrail])
  const hasTrailSystemPermissions = UserService.hasPermissions(user, [AppPermissions.viewTrailSystem])

  const [isSaving, setIsSaving] = useState(false)
  const { isEditing, editSection, openEditor, closeEditor } = useEditSectionParam()
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const sponsorService = useMemo(() => new SponsorService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName(`Sponsor`)
  }, [id])

  const queryClient = useQueryClient()
  const { isLoading, data: sponsor } = useQuery(
    [
      'SponsorService.get',
      {
        id: Number(id),
        relations: [SponsorRelations.system, SponsorRelations.trail],
      },
      AppStateContext.getRegion(),
    ],
    () =>
      sponsorService.get({
        id: Number(id),
        relations: [SponsorRelations.system, SponsorRelations.trail],
      }),
    {
      onError: (err) => {
        setLoadingError(typeof err === 'string' ? err : 'Failed to load sponsor.')
        console.error(err)
      },
    },
  )

  const handleEdit = () => {
    closeEditor()
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDelete = () => {
    if (sponsor?.id) {
      setIsSaving(true)
      setIsDeleteConfirmationOpen(false)
      sponsorService
        .delete(sponsor.id)
        .then(() => {
          SnackbarContext.show(
            `Sponsor deleted successfully. You will now be redirected.`,
            'success',
            APP_UPDATE_NOTICE,
          )

          // after a delay of 1 second, redirect to the sponsor list page
          setTimeout(() => {
            navigate(`/sponsors`)
            queryClient.removeQueries([
              'WeatherGroupService.get',
              {
                id: Number(id),
              },
            ])
            // invalidate all queries to ensure any queries that are using the modified record are updated
            queryClient.invalidateQueries()
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Sponsor failed to delete: ${err}`, 'error')
          setIsSaving(false)
        })
    }
  }

  // a trail-level sponsor has no system of its own, so fall back to the trail's system when it was returned
  const assignedSystem = sponsor?.system ?? sponsor?.trail?.system
  const sectionById = (sectionId: SponsorSectionIds) =>
    sponsorSections.find((section) => section.id === sectionId) as SectionDefinition
  // only offer per-section edit shortcuts to users who can edit
  const editSectionHandler = (sectionId: SponsorSectionIds) =>
    hasWritePermissions ? () => openEditor(sectionId) : undefined

  return (
    <>
      {sponsor && (sponsor.system || sponsor.trail) && (
        <FormDialog<Sponsor> open={isEditing} entity={sponsor} entityLabel="Sponsor" onClose={closeEditor}>
          <SponsorForm
            sponsor={sponsor}
            trail={sponsor.trail}
            trailSystem={sponsor.system}
            initialSection={editSection}
            onEdit={handleEdit}
            onCancel={closeEditor}
          />
        </FormDialog>
      )}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>Are you sure you want to delete this sponsor?</DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setIsDeleteConfirmationOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ p: 2 }}>
        {isLoading && !loadingError && <Loading sx={{ py: 20 }} />}
        {loadingError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && sponsor && !sponsor.system && !sponsor.trail && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">
              Failed to locate the assigned trail system or trail for the requested sponsor.
            </Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && sponsor && (sponsor.system || sponsor.trail) && (
          <>
            {isSaving && <Loading sx={{ py: 20 }} />}
            {!isSaving && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs
                    items={() => {
                      const breadcrumbs: Array<{ label: string; href?: string }> = [
                        {
                          label: 'Sponsors',
                          href: '/sponsors',
                        },
                      ]
                      if (hasTrailSystemPermissions && sponsor.system) {
                        breadcrumbs.push({
                          label: sponsor.system.name || 'Trail System',
                          href: `/trail-systems/${sponsor.system.id}`,
                        })
                      }
                      if (hasTrailPermissions && sponsor.trail) {
                        breadcrumbs.push({
                          label: sponsor.trail.name || 'Trail',
                          href: `/trails/${sponsor.trail.id}`,
                        })
                      }
                      breadcrumbs.push({ label: `Sponsor ID: ${sponsor.id}` })

                      return breadcrumbs
                    }}
                  />
                  {hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      <Button variant="text" onClick={handleDeleteClick}>
                        Delete
                      </Button>
                      <Button variant="contained" onClick={() => openEditor()}>
                        Edit
                      </Button>
                    </Stack>
                  )}
                </Stack>
                <DetailHeader
                  title={sponsor.name}
                  active={!!sponsor.active}
                  subtitle={`${sponsor.region} · ID ${sponsor.id}`}
                  meta={[`Updated ${formatDate(sponsor.updatedAt)}`, `Created ${formatDate(sponsor.createdAt)}`]}
                />

                <Grid container spacing={2}>
                  {/* --- General (left) ------------------------------------------ */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(SponsorSectionIds.general)}
                      onEdit={editSectionHandler(SponsorSectionIds.general)}
                    >
                      {sponsor.logoUrl && (
                        <LightboxImage
                          src={sponsor.logoUrl}
                          alt={`${sponsor.name} logo`}
                          sx={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain', alignSelf: 'flex-start' }}
                        />
                      )}
                      <DetailField label="Priority">{sponsor.priority ?? '-'}</DetailField>
                      <DetailField label="URL">
                        {sponsor.url ? (
                          <Link href={sponsor.url} target="_blank" rel="noopener noreferrer">
                            {sponsor.url}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </DetailField>
                      <DetailField label="Logo URL">
                        {sponsor.logoUrl ? (
                          <Link href={sponsor.logoUrl} target="_blank" rel="noopener noreferrer">
                            {sponsor.logoUrl}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </DetailField>
                    </DetailSection>
                  </Grid>

                  {/* --- Applies To + Schedule (right) ---------------------------- */}
                  <Grid item zero={12} md={6}>
                    <Stack spacing={2}>
                      <DetailSection
                        section={sectionById(SponsorSectionIds.assignment)}
                        onEdit={editSectionHandler(SponsorSectionIds.assignment)}
                        sx={{ height: 'auto' }}
                      >
                        <DetailFieldGrid>
                          <DetailField label="Trail System">
                            {assignedSystem?.id ? (
                              hasTrailSystemPermissions ? (
                                <Link href={`/trail-systems/${assignedSystem.id}`}>{assignedSystem.name}</Link>
                              ) : (
                                assignedSystem.name
                              )
                            ) : (
                              '-'
                            )}
                          </DetailField>
                          <DetailField label="Trail">
                            {sponsor.trail?.id ? (
                              hasTrailPermissions ? (
                                <Link href={`/trails/${sponsor.trail.id}`}>{sponsor.trail.name}</Link>
                              ) : (
                                sponsor.trail.name
                              )
                            ) : (
                              'All trails in the system'
                            )}
                          </DetailField>
                        </DetailFieldGrid>
                      </DetailSection>
                      <DetailSection
                        section={sectionById(SponsorSectionIds.schedule)}
                        onEdit={editSectionHandler(SponsorSectionIds.schedule)}
                        sx={{ height: 'auto' }}
                      >
                        <DetailFieldGrid minColumnWidth={140}>
                          <DetailField label="Active">
                            <Chip
                              size="small"
                              variant="outlined"
                              label={sponsor.active ? 'Active' : 'Inactive'}
                              color={sponsor.active ? 'success' : 'error'}
                            />
                          </DetailField>
                          <DetailField label="Start">
                            {sponsor.start ? formatDate(sponsor.start) : 'Immediately'}
                          </DetailField>
                          <DetailField label="End">{sponsor.end ? formatDate(sponsor.end) : 'Never'}</DetailField>
                        </DetailFieldGrid>
                      </DetailSection>
                    </Stack>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default SponsorDetail
